import { inject, Injectable, untracked } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { Query, QueryRepository, Response } from '@okcargo/query-processor';
import { EMPTY, filter, firstValueFrom, fromEvent, takeUntil } from 'rxjs';
import { ApiService } from '../api/api.service';
import { ConnectivityService } from '../connectivity';

@Injectable({ providedIn: 'root' })
export class BaseQueryRepository implements QueryRepository {
  private readonly apiService = inject(ApiService);
  private readonly connectivity = inject(ConnectivityService);
  private readonly isOnline$ = toObservable(this.connectivity.isOnline).pipe(filter(Boolean));
  private readonly _baseUrl = '/service/query-processor/api/queries';

  private readonly DOWNLOAD_QUERIES = new Set<string>([
    'GenericDownloadDocumentQuery',
    'GenerateAndDownloadLoadingOrderQuery',
    'GenerateAndDownloadEcmrQuery',
    'GenerateAndDownloadDigitalControlDocQuery',
  ]);

  /**
   * Resolves as soon as there is connectivity, or immediately when there already is. It rejects
   * with an AbortError when the request is aborted while waiting, so the caller (e.g. a resource)
   * can discard it.
   */
  private async waitForConnection(abortSignal?: AbortSignal): Promise<void> {
    // untracked: queries are executed from reactive contexts (e.g. a service effect that loads its
    // data). Reading the connectivity status here would make that context depend on it, so every
    // connection change would re-run it and re-issue the query. The waiting itself is done by
    // `isOnline$`, which is not reactive.
    if (!untracked(() => this.connectivity.isOffline())) {
      return;
    }

    try {
      await firstValueFrom(this.isOnline$.pipe(takeUntil(abortSignal ? fromEvent(abortSignal, 'abort') : EMPTY)));
    } catch (error) {
      if (abortSignal?.aborted) {
        throw new DOMException('The operation was aborted.', 'AbortError');
      }

      throw error;
    }
  }

  public buildUrl(name: string, query: Query): string {
    const isDownloadQuery = this.DOWNLOAD_QUERIES.has(name);
    const baseUrl = isDownloadQuery ? `${this._baseUrl}/download` : this._baseUrl;

    const queryRecord = query as unknown as Record<string, unknown>;
    const { abortSignal, ...queryPayload } = queryRecord;

    return `${baseUrl}?name=${name}&payload=${encodeURI(JSON.stringify(queryPayload))}`;
  }

  public async execute<T extends Response>(name: string, query: Query): Promise<T> {
    const queryRecord = query as unknown as Record<string, unknown>;
    const { abortSignal } = queryRecord;

    // A query is a read and it is not lost without connectivity: it waits for the connection and
    // runs then, so the screens keep their loading state (skeletons) instead of failing.
    await this.waitForConnection(abortSignal as AbortSignal | undefined);

    const isDownloadQuery = this.DOWNLOAD_QUERIES.has(name);
    const fullUrl = this.buildUrl(name, query);

    if (isDownloadQuery) {
      return firstValueFrom(
        this.apiService.get(fullUrl, {
          observe: 'response',
          responseType: 'blob' as 'json',
          signal: abortSignal as AbortSignal | undefined,
        }),
      ) as unknown as Promise<T>;
    }

    return firstValueFrom(
      this.apiService.get(fullUrl, {
        observe: 'body',
        signal: abortSignal as AbortSignal | undefined,
      }),
    ) as unknown as Promise<T>;
  }
}
